import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Backdrop,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";

import AuthContext from "../../../context/AuthContext";

import IsoFields from "./IsoFields";
import BankFields from "./BankFieldsISO";

import { blokkoStepperSx, blokkoStepLabelSx } from "../../../theme/blokkoStepper";
import { fetchData } from "../../../services/apiService";
import {
  createUserInKeycloak,
  checkUserExists,
} from "../../../services/userService";
import { useEffectOnce } from "../../../utils/effects";
import { useSelector, useDispatch } from "react-redux";
import {
  getEnrollmentByIdLoad,
  createEnrollmentLoad,
  updateEnrollmentLoad,
} from "../../lists/LoadEnrollment/LoadEnrollment.thunk";
import {
  SetEnrollmentSelected,
  ResetDataFilteredByid,
} from "../../lists/LoadEnrollment/LoadEnrollment.slice";
import {
  setIsoEnrollmentSelected,
  setIsoEnrollmentDataById,
} from "../../lists/IsoEnrollment/IsoEnrollment.slice";
import {
  updateIso,
  getIsoEnrollmentList,
} from "../../lists/IsoEnrollment/IsoEnrollment.thunk";
import CriptoWallets from "../../wallets/CriptoWallets";
import {
  isoInitialValues,
  isoValidationSchema,
  setIsoValues,
  prepareIsoDataForSubmit,
} from "./IsoEnrollmentForm.resources";

const steps = ["ISO", "Bank Info"];

const FormComponent = ({
  goBack,
  setSnackOpen,
  setMessageOpen,
  setSeverity,
}) => {
  const dispatch = useDispatch();
  const { token, acquirerData, userInfo } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { enrollmentDataById, statusRetrieve, enrollmentSelected } =
    useSelector((state) => state.loadEnrollment);
  const {
    isoEnrollmentDataById,
    isoEnrollmentSelected,
    statusUpdate,
    page,
    pageSize,
  } = useSelector((state) => state.isoEnrollment);

  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;

  // Inicializar Formik
  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
    isSubmitting,
  } = useFormik({
    initialValues: isoInitialValues,
    validationSchema: isoValidationSchema(isEditMode),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  useEffectOnce(async () => {
    if (enrollmentSelected) {
      dispatch(getEnrollmentByIdLoad({ token, id: enrollmentSelected }));
    }
  });

  // Cargar datos desde LoadEnrollment (Draft)
  useEffect(() => {
    if (
      enrollmentSelected &&
      enrollmentDataById &&
      statusRetrieve === "succeeded"
    ) {
      const { data, current_step } = enrollmentDataById;
      setIsoValues({ isoData: data, setFieldValue });
      setActiveStep(current_step > 0 ? current_step - 1 : current_step);
      dispatch(SetEnrollmentSelected({ enrollmentSelected: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentDataById, enrollmentSelected, statusRetrieve]);

  // Cargar datos del ISO en modo edición
  useEffect(() => {
    if (isoEnrollmentDataById && isoEnrollmentSelected) {
      setIsEditMode(true);
      setIsoValues({ isoData: isoEnrollmentDataById, setFieldValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoEnrollmentDataById, isoEnrollmentSelected]);

  // Manejar el resultado de la actualización
  useEffect(() => {
    if (statusUpdate === "succeeded") {
      setMessageOpen(`✅ ISO actualizado exitosamente!`);
      setSeverity("success");
      setSnackOpen(true);
      // Recargar la lista
      dispatch(
        getIsoEnrollmentList({ token, acquirerData, page, page_size: pageSize })
      );

      // Limpiar el formulario y el modo edición
      setIsEditMode(false);
      dispatch(setIsoEnrollmentSelected(null));
      dispatch(setIsoEnrollmentDataById(null));
    } else if (statusUpdate === "failed") {
      setMessageOpen("Error al actualizar ISO");
      setSeverity("error");
      setSnackOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusUpdate]);

  useEffect(() => {
    return () => {
      dispatch(ResetDataFilteredByid());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Valida el paso actual antes de permitir avanzar al siguiente paso
   * @returns {Promise<boolean>} - True si la validación pasa, false de lo contrario
   */
  const validateCurrentStep = async () => {
    try {
      // Obtener todos los errores de validación
      const validationErrors = await validateForm();

      // Definir qué campos pertenecen a cada paso
      const stepFields = {
        ISO: [
          "isoName",
          "iso.email",
          "iso.currencyCode",
          "iso.address",
          "iso.city",
          "iso.state",
          "iso.postalCode",
          "iso.countryCode",
          "iso.amountTxProcessing",
          "iso.percentTxProcessing",
        ],
        "Bank Info": [
          "bank.Iban",
          "bank.address",
          "bank.accountNumber",
          "bank.routingNumber",
        ],
      };

      // Obtener el nombre del paso actual
      const currentStepName = steps[activeStep];
      const currentStepFields = stepFields[currentStepName] || [];

      // Verificar si hay errores en los campos del paso actual
      const stepErrors = currentStepFields.filter((fieldPath) => {
        // Convertir notación de punto a acceso de objeto anidado para verificar errores
        const fieldErrors = fieldPath
          .split(".")
          .reduce((obj, key) => obj?.[key], validationErrors);
        return fieldErrors !== undefined;
      });

      if (stepErrors.length > 0) {
        // Usar Promise.all para asegurar que todos los campos están marcados como touched
        await Promise.all(
          currentStepFields.map(async (fieldPath) => {
            // Marcar campo como touched y activar validación
            setFieldTouched(fieldPath, true, false);
            // También activar validación estableciendo el valor del campo nuevamente
            const currentValue = fieldPath
              .split(".")
              .reduce((obj, key) => obj?.[key], values);
            setFieldValue(fieldPath, currentValue, true);
          })
        );

        // Forzar un re-render activando validación nuevamente
        await validateForm();

        // Agregar un pequeño delay para asegurar que las actualizaciones de estado se procesen
        await new Promise((resolve) => setTimeout(resolve, 100));

        return false;
      }

      return true;
    } catch (error) {
      console.error("Error de validación:", error);
      return false;
    }
  };

  const handleNext = async () => {
    const isStepValid = await validateCurrentStep();

    if (isStepValid) {
      try {
        if (!isEditMode) {
          if (!enrollmentDataById?.id) {
            dispatch(
              createEnrollmentLoad({
                token,
                formData: values,
                userMail: userInfo?.email || "",
                currentStep: activeStep + 1,
                enrollment_type: "ISO",
              })
            );
          } else {
            dispatch(
              updateEnrollmentLoad({
                token,
                formData: values,
                userMail: userInfo?.email || "",
                enrollmentId: {
                  id: enrollmentSelected || enrollmentDataById?.id,
                },
                currentStep: activeStep + 1,
                enrollment_type: "ISO",
              })
            );
          }
        }
        setActiveStep(activeStep + 1);
      } catch (error) {
        console.error("Error guardando enrollment:", error);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleFormSubmit = async (formValues) => {
    setSubmitting(true);
    let successMessage = "";

    // Si está en modo edición, usar el thunk de actualización
    if (isEditMode && isoEnrollmentDataById) {
      setLoading(true);
      try {
        const updatedIsoData = prepareIsoDataForSubmit(
          formValues,
          isoEnrollmentDataById.isoID
        );
        await dispatch(
          updateIso({ token, acquirerData, isoData: updatedIsoData })
        ).unwrap();
        successMessage = `✅ ISO successfully updated! ISO ID: ${isoEnrollmentDataById.isoID}`;
        setMessageOpen(successMessage);
        setSeverity("success");
        setSnackOpen(true);
        goBack();
      } catch (error) {
        console.error("Error updating ISO:", error);
        setSeverity("error");
        setMessageOpen("Error updating ISO!");
        setSnackOpen(true);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
      return;
    }

    // Modo creación (lógica original)
    try {
      setLoading(true);
      const rol_data = "ISO";
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        acquirerName: acquirerData.acquirerName,
        acquirerID: acquirerData.acquirerId,
      };

      const { currencyCode, email } = formValues.iso;
      const isoName = formValues.isoName;
      const now = new Date();
      const formattedEffectiveDate = `${now.getFullYear()}${String(
        now.getMonth() + 1
      ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;

      const sanitizedUsername = `${acquirerData.acquirerId}_${rol_data}_${isoName.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")}`;

      // Check if user already exists before creating ISO
      const existingUser = await checkUserExists({
        username: sanitizedUsername,
        email,
        token,
        acquirerData,
      });
      if (existingUser) {
        setSeverity("error");
        setMessageOpen("User already exists with this email or ISO Name!");
        setSnackOpen(true);
        setSubmitting(false);
        return;
      }

      const payload = {
        version: "04.09.01",
        acquirerID: acquirerData.acquirerId,
        fiat: {
          currencyCode,
        },
        isoName: isoName,
        iso: {
          ...formValues.iso,
          effectiveDate: formattedEffectiveDate,
          amountTerminalMaintenance: "0",
          terminalMaintenanceInactive: "F",
          amountTerminalSetup: "0",
        },
        bank: {
          ...formValues.bank,
        },
        crypto_wallets: formValues.crypto_wallets || [],
        timeStamp: new Date().toISOString(),
      };

      // create ISO
      const response = await fetchData({
        endpoint: "/enroll-iso",
        data: payload,
        headers,
        token,
      });

      successMessage = `✅ ISO successfully saved! ISO ID: ${response.data.isoID}`;

      if (response.data.isoID) {
        // create user
        const userData = {
          username: sanitizedUsername,
          email: email,
          emailVerified: false,
          enabled: true,
          credentials: [
            {
              type: "password",
              value: `${acquirerData.acquirerId}${rol_data}123*`,
              temporary: true,
            },
          ],
          attributes: {
            "acquirer-rol-data": rol_data,
            "acquirer-id-data": acquirerData.acquirerId,
            "acquirer-name-data": acquirerData.acquirerName,
            "acquirer-id-iso-data": response.data.isoID,
            "acquirer-currency-data": currencyCode,
          },
        };

        try {
          await createUserInKeycloak(userData, token, acquirerData);

          successMessage += `\n👤 User successfully created! Check your email to log in!`;
        } catch (keycloakError) {
          console.error("Error creating user in Keycloak:", keycloakError);
          setSeverity("error");
          setMessageOpen(
            `ISO created, ISO ID: ${response.data.isoID}, but user not created because there is already a user with that email or name!`
          );
          setSnackOpen(true);
          setSubmitting(false);
          return;
        }
      }

      setMessageOpen(successMessage);
      setSeverity("success");
      setSnackOpen(true);
      goBack();
    } catch (error) {
      let errorMessage = "ISO NOT saved!";
      if (error.request?.responseText) {
        try {
          const response = JSON.parse(error.request.responseText);
          if (response.detail) {
            errorMessage += `: ${response.detail}`;
          }
        } catch (parseError) {
          errorMessage += `: ${error.request.responseText}`;
        }
      }
      setSeverity("error");
      setMessageOpen(errorMessage);
      setSnackOpen(true);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleStepClick = (index) => {
    if (index < activeStep) {
      setActiveStep(index);
      return;
    }
    setActiveStep(index);
  };

  const handleSave = async () => {
    const isStepValid = await validateCurrentStep();
    if (isStepValid) {
      handleSubmit();
    }
  };

  return (
    <Box sx={{ width: "100%", py: 4 }}>
      <Backdrop
        open={loading || statusUpdate === "loading"}
        sx={{ color: "common.white", zIndex: 1301 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {isEditMode && (
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
        >
          Editar ISO - {isoEnrollmentDataById?.isoID}
        </Typography>
      )}

      <Stepper activeStep={activeStep} alternativeLabel sx={blokkoStepperSx}>
        {steps.map((label, index) => (
          <Step key={index}>
            <StepLabel
              sx={blokkoStepLabelSx(activeStep === index)}
              onClick={() => handleStepClick(index)}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Grid
        container
        spacing={isMobile ? 2 : 4}
        pb={isMobile ? "16px" : "32px"}
      >
        {activeStep === steps.indexOf("ISO") && (
          <IsoFields
            formValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            isEditMode={isEditMode}
          />
        )}
        {activeStep === steps.indexOf("Bank Info") && (
          <>
            <BankFields
              formValues={values}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
            />
            <Grid item xs={12}>
              <CriptoWallets
                formValues={values}
                handleChange={handleChange}
                errors={errors}
                touched={touched}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={goBack}
            size="small"
            sx={{ marginRight: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={isFirstStep}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            disabled={isSubmitting || loading || statusUpdate === "loading"}
            variant="contained"
            color="primary"
            onClick={isLastStep ? handleSave : handleNext}
          >
            {!isLastStep ? "Next" : isEditMode ? "Actualizar ISO" : "Finish"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FormComponent;
